import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CreditCard, Shield, CheckCircle2, AlertCircle, Building2, Wallet, Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatDate, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { track } from "@/lib/analytics";

export default function CheckoutPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth() || {};
    const { t } = useTranslation();
    const { format, banks, posBanks, paymentMethods } = useCurrency();

    const hotelId = params.get("hotel_id");
    const roomTypeId = params.get("room_type_id");
    const ratePlanId = params.get("rate_plan_id") || null;
    const checkIn = params.get("check_in");
    const checkOut = params.get("check_out");
    const adults = Number(params.get("adults") || 2);
    const childrenCount = Number(params.get("children") || 0);
    const childAges = (params.get("child_ages") || "").split(",").filter(Boolean).map(Number);
    const rooms = Number(params.get("rooms") || 1);
    const cancelled = params.get("cancelled");

    const [hotel, setHotel] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [quote, setQuote] = useState(null);

    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: user?.email || "",
        phone: "",
        special_requests: "",
    });
    const [insurance, setInsurance] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("card"); // card | bank_transfer | at_hotel
    const [selectedPosBank, setSelectedPosBank] = useState(null);
    const [selectedBankId, setSelectedBankId] = useState(null);
    const [copiedIban, setCopiedIban] = useState(null);

    useEffect(() => {
        if (!hotelId) return;
        api.get(`/hotels/${hotelId}`).then((r) => {
            setHotel(r.data);
            track("booking_started", { hotel_id: r.data.id, hotel_name: r.data.name });
        });
    }, [hotelId]);

    // Live quote
    useEffect(() => {
        if (!hotelId || !roomTypeId) return;
        api.post("/bookings/quote", {
            hotel_id: hotelId,
            room_type_id: roomTypeId,
            rate_plan_id: ratePlanId,
            check_in: checkIn,
            check_out: checkOut,
            adults,
            children: childrenCount,
            child_ages: childAges,
            rooms,
            insurance,
        }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, [hotelId, roomTypeId, ratePlanId, checkIn, checkOut, adults, childrenCount, rooms, insurance]);

    useEffect(() => {
        if (user) {
            const [fn, ...rest] = (user.full_name || "").split(" ");
            setForm((f) => ({ ...f, first_name: fn || "", last_name: rest.join(" "), email: user.email }));
        }
    }, [user]);

    // Default-select first bank when switching to bank_transfer
    useEffect(() => {
        if (paymentMethod === "bank_transfer" && banks.length > 0 && !selectedBankId) {
            setSelectedBankId(banks[0].id);
        }
    }, [paymentMethod, banks, selectedBankId]);

    // Auto-pick first enabled payment method (card replaces old stripe)
    useEffect(() => {
        if (!paymentMethods[paymentMethod]) {
            const first = ["card", "bank_transfer", "at_hotel"].find((k) => paymentMethods[k]);
            if (first) setPaymentMethod(first);
        }
    }, [paymentMethods]); // eslint-disable-line

    // Auto-pick first POS bank when switching to card
    useEffect(() => {
        if (paymentMethod === "card" && posBanks.length > 0 && !selectedPosBank) {
            setSelectedPosBank(posBanks[0].code);
        }
    }, [paymentMethod, posBanks, selectedPosBank]);

    const room = hotel?.room_types?.find((r) => r.id === roomTypeId);
    const selectedPlan = room?.rate_plans?.find((p) => p.id === ratePlanId);
    const nights = quote?.nights ?? Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)));
    const originalSubtotal = quote?.original_subtotal ?? quote?.subtotal ?? (room ? room.price_per_night * nights * rooms : 0);
    const subtotal = quote?.subtotal ?? originalSubtotal;
    const discountAmount = quote?.discount_amount ?? 0;
    const discountsApplied = quote?.discounts_applied ?? [];
    const fakeOriginalTotal = quote?.fake_original_total ?? null;
    const insuranceAmount = quote?.insurance_amount ?? (insurance ? Math.round(subtotal * 0.01 * 100) / 100 : 0);
    const total = quote?.total ?? (subtotal + insuranceAmount);

    const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const copyIban = async (iban, id) => {
        try {
            await navigator.clipboard.writeText(iban);
            setCopiedIban(id);
            setTimeout(() => setCopiedIban(null), 1500);
        } catch (e) { /* noop */ }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.first_name || !form.last_name || !form.email || !form.phone) {
            setError(t("checkout.error_fill_required"));
            return;
        }
        if (paymentMethod === "bank_transfer" && banks.length > 0 && !selectedBankId) {
            setError(t("checkout.error_select_bank"));
            return;
        }
        if (paymentMethod === "card" && posBanks.length > 0 && !selectedPosBank) {
            setError("Lütfen sanal POS bankası seçin");
            return;
        }
        setSubmitting(true);
        try {
            // 1) Create booking
            const bookingResp = await api.post("/bookings", {
                hotel_id: hotelId,
                room_type_id: roomTypeId,
                rate_plan_id: ratePlanId,
                check_in: checkIn,
                check_out: checkOut,
                adults,
                children: childrenCount,
                child_ages: childAges,
                rooms,
                guest: {
                    first_name: form.first_name,
                    last_name: form.last_name,
                    email: form.email,
                    phone: form.phone,
                },
                special_requests: form.special_requests || null,
                insurance,
                payment_method: paymentMethod,
                selected_bank_id: paymentMethod === "bank_transfer" ? selectedBankId : null,
                payment_bank: paymentMethod === "card" ? selectedPosBank : null,
                origin_url: window.location.origin,
                ...(() => {
                    // Forward widget attribution captured on HotelDetailPage so the
                    // booking is recorded as source=widget with its referrer hotel.
                    try {
                        const raw = sessionStorage.getItem("msonline_widget_ref");
                        if (!raw) return {};
                        const w = JSON.parse(raw);
                        // Only honor recent (< 2h) widget context to avoid stale attribution
                        if (!w || (Date.now() - (w.ts || 0)) > 2 * 3600 * 1000) return {};
                        return {
                            widget_source: "widget",
                            widget_variant: w.variant || null,
                            widget_theme: w.theme || null,
                            referrer_hotel_id: w.referrer_hotel_id || null,
                        };
                    } catch (e) { return {}; }
                })(),
            });
            const booking = bookingResp.data;

            // For card / bank_transfer / at_hotel — go directly to success page with booking_id
            // (Real virtual POS redirect will be wired when iyzico/PayTR integration is enabled)
            navigate(`/booking-success?booking_id=${booking.id}&pm=${paymentMethod}${paymentMethod === "card" && selectedPosBank ? `&bank=${selectedPosBank}` : ""}`);
        } catch (err) {
            setError(err.response?.data?.detail || t("checkout.error_fill_required"));
            setSubmitting(false);
        }
    };

    if (!hotel || !room) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
                <Navbar />
                <div className="container mx-auto px-4 max-w-7xl py-12">{t("common.loading")}</div>
            </div>
        );
    }

    const submitLabel = submitting ? t("checkout.cta_processing")
        : paymentMethod === "card" ? `Ödemeye Geç · ${format(total)}`
        : paymentMethod === "bank_transfer" ? t("checkout.cta_confirm_bank_transfer", { amount: format(total) })
        : t("checkout.cta_confirm_at_hotel", { amount: format(total) });

    return (
        <div data-testid="checkout-page" className="bg-slate-50 dark:bg-slate-950 min-h-screen">
            <Navbar />
            <div className="container mx-auto px-4 md:px-6 max-w-6xl py-8">
                <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6">{t("checkout.title")}</h1>

                {cancelled && (
                    <div data-testid="cancelled-banner" className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 flex items-center gap-2">
                        <AlertCircle className="h-5 w-5" /> {t("checkout.cancelled_banner")}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Form */}
                    <form onSubmit={onSubmit} className="lg:col-span-2 space-y-6">
                        <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                            <h2 className="font-display text-xl font-bold mb-4 text-slate-900 dark:text-white">{t("checkout.guest_info")}</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input label={t("checkout.first_name")} testid="checkout-firstname" value={form.first_name} onChange={(v) => onChange("first_name", v)} required />
                                <Input label={t("checkout.last_name")} testid="checkout-lastname" value={form.last_name} onChange={(v) => onChange("last_name", v)} required />
                                <Input label={t("checkout.email")} testid="checkout-email" type="email" value={form.email} onChange={(v) => onChange("email", v)} required />
                                <Input label={t("checkout.phone")} testid="checkout-phone" type="tel" value={form.phone} onChange={(v) => onChange("phone", v)} placeholder="+90 5XX XXX XX XX" required />
                            </div>
                        </div>

                        <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                            <h2 className="font-display text-xl font-bold mb-2 text-slate-900 dark:text-white">{t("checkout.special_requests")}</h2>
                            <textarea
                                data-testid="checkout-requests"
                                rows={3}
                                value={form.special_requests}
                                onChange={(e) => onChange("special_requests", e.target.value)}
                                placeholder={t("checkout.special_requests_placeholder")}
                                className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
                            />
                        </div>

                        {/* Cancellation insurance */}
                        <label
                            data-testid="insurance-card"
                            className={`block p-5 rounded-2xl border-2 cursor-pointer transition-colors ${insurance ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-500" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300"}`}
                        >
                            <div className="flex items-start gap-3">
                                <input
                                    data-testid="insurance-toggle"
                                    type="checkbox"
                                    checked={insurance}
                                    onChange={(e) => setInsurance(e.target.checked)}
                                    className="mt-1 h-5 w-5 accent-emerald-500"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Shield className="h-4 w-4 text-emerald-600" />
                                        <span className="font-display font-bold text-slate-900 dark:text-white">{t("checkout.insurance_title")}</span>
                                        <span className="ml-auto text-sm font-bold text-emerald-600 dark:text-emerald-400">{format(Math.round(subtotal * 0.01 * 100) / 100)}</span>
                                    </div>
                                    <p className="text-xs text-slate-600 dark:text-slate-400">{t("checkout.insurance_desc")}</p>
                                </div>
                            </div>
                        </label>

                        {/* Payment Method Picker */}
                        <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                            <h2 className="font-display text-xl font-bold mb-4 text-slate-900 dark:text-white flex items-center gap-2">
                                <CreditCard className="h-5 w-5 text-sky-500" /> {t("checkout.payment_title")}
                            </h2>

                            <div className="space-y-3" data-testid="payment-methods">
                                {paymentMethods.card && (
                                    <PaymentRadio
                                        testId="pm-card"
                                        active={paymentMethod === "card"}
                                        onClick={() => setPaymentMethod("card")}
                                        icon={<CreditCard className="h-5 w-5 text-sky-500" />}
                                        title="Kredi / Banka Kartı"
                                        desc="Sanal POS ile güvenli ödeme"
                                    />
                                )}
                                {paymentMethods.bank_transfer && (
                                    <PaymentRadio
                                        testId="pm-bank"
                                        active={paymentMethod === "bank_transfer"}
                                        onClick={() => setPaymentMethod("bank_transfer")}
                                        icon={<Building2 className="h-5 w-5 text-amber-500" />}
                                        title={t("checkout.pay_with_transfer")}
                                        desc={t("checkout.pay_with_transfer_desc")}
                                    />
                                )}
                                {paymentMethods.at_hotel && (
                                    <PaymentRadio
                                        testId="pm-hotel"
                                        active={paymentMethod === "at_hotel"}
                                        onClick={() => setPaymentMethod("at_hotel")}
                                        icon={<Wallet className="h-5 w-5 text-emerald-500" />}
                                        title={t("checkout.pay_at_hotel")}
                                        desc={t("checkout.pay_at_hotel_desc")}
                                    />
                                )}
                            </div>

                            {/* Method-specific info */}
                            {paymentMethod === "card" && (
                                <div className="mt-4 space-y-3">
                                    <p className="text-xs text-slate-600 dark:text-slate-300">Hangi banka ile ödeme yapacaksınız? Kartınız hangi bankaya aitse seçin.</p>
                                    {posBanks.length === 0 ? (
                                        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                                            Sanal POS bankaları henüz tanımlanmamış. Lütfen yöneticiye ulaşın.
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="pos-bank-list">
                                            {posBanks.map((b) => {
                                                const active = selectedPosBank === b.code;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={b.code}
                                                        data-testid={`pos-bank-${b.code}`}
                                                        onClick={() => setSelectedPosBank(b.code)}
                                                        className={`p-3 rounded-xl border-2 transition-colors text-left ${active ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30" : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 hover:border-slate-300"}`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <CreditCard className={`h-4 w-4 ${active ? "text-sky-500" : "text-slate-400"}`} />
                                                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{b.label}</span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <p className="text-xs text-slate-500 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-emerald-500" /> Ödemeniz seçtiğiniz bankanın güvenli sanal POS sayfasında gerçekleşir.</p>
                                </div>
                            )}

                            {paymentMethod === "at_hotel" && (
                                <div className="mt-4 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-xs text-emerald-800 dark:text-emerald-300">
                                    {t("checkout.at_hotel_note")}
                                </div>
                            )}

                            {paymentMethod === "bank_transfer" && (
                                <div className="mt-4 space-y-3">
                                    <p className="text-xs text-slate-600 dark:text-slate-300">{t("checkout.bank_note_intro")}</p>
                                    {banks.length === 0 ? (
                                        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                                            Banka bilgileri henüz tanımlanmamış. Lütfen yöneticiye ulaşın.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{t("checkout.bank_select_label")}</div>
                                            <div className="space-y-2" data-testid="bank-list">
                                                {banks.map((b) => {
                                                    const active = selectedBankId === b.id;
                                                    return (
                                                        <div
                                                            key={b.id}
                                                            data-testid={`bank-${b.id}`}
                                                            onClick={() => setSelectedBankId(b.id)}
                                                            className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${active ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30" : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 hover:border-slate-300"}`}
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="font-display font-bold text-slate-900 dark:text-white">{b.bank_name}</div>
                                                                    <div className="text-xs text-slate-500 mt-0.5">{t("checkout.bank_account_holder")}: <span className="font-medium text-slate-700 dark:text-slate-200">{b.account_holder}</span></div>
                                                                    {b.branch && <div className="text-xs text-slate-500">{t("checkout.bank_branch")}: <span className="font-medium text-slate-700 dark:text-slate-200">{b.branch}</span></div>}
                                                                    {b.swift && <div className="text-xs text-slate-500">{t("checkout.bank_swift")}: <span className="font-mono font-medium text-slate-700 dark:text-slate-200">{b.swift}</span></div>}
                                                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{t("checkout.bank_iban")}</span>
                                                                        <code className="text-xs font-mono px-2 py-1 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white">{b.iban}</code>
                                                                        <button
                                                                            type="button"
                                                                            data-testid={`copy-iban-${b.id}`}
                                                                            onClick={(e) => { e.stopPropagation(); copyIban(b.iban, b.id); }}
                                                                            className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${copiedIban === b.id ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300" : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600"}`}
                                                                        >
                                                                            {copiedIban === b.id ? <><Check className="h-3 w-3" /> {t("checkout.bank_copied")}</> : <><Copy className="h-3 w-3" /> {t("checkout.bank_copy")}</>}
                                                                        </button>
                                                                    </div>
                                                                    {b.note && <div className="mt-1 text-[11px] italic text-slate-500">{b.note}</div>}
                                                                </div>
                                                                <div className={`mt-1 h-5 w-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${active ? "border-sky-500 bg-sky-500" : "border-slate-300 dark:border-slate-600"}`}>
                                                                    {active && <Check className="h-3 w-3 text-white" />}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {error && (
                                <div data-testid="checkout-error" className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
                            )}
                            <button
                                type="submit"
                                data-testid="checkout-submit"
                                disabled={submitting}
                                className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-semibold disabled:opacity-60"
                            >
                                {submitLabel}
                            </button>
                        </div>
                    </form>

                    {/* Summary */}
                    <aside className="lg:col-span-1">
                        <div className="sticky top-24 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <img src={resolveImageUrl(hotel.main_image)} alt={hotel.name} className="w-full h-40 object-cover" />
                            <div className="p-5 space-y-3">
                                <div>
                                    <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white leading-tight">{hotel.name}</h3>
                                    <div className="text-sm text-slate-500">{hotel.destination_name}</div>
                                </div>
                                <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2 text-sm">
                                    <Row label={t("common.from")} value={formatDate(checkIn)} />
                                    <Row label={t("common.to")} value={formatDate(checkOut)} />
                                    <Row label={t("common.night")} value={`${nights} ${t("common.nights")}`} />
                                    <Row label={t("common.guests")} value={`${adults} ${t("common.adults").toLowerCase()}${childrenCount ? `, ${childrenCount} ${t("common.children").toLowerCase()} (${childAges.join(", ")})` : ""} · ${rooms} ${t("common.rooms").toLowerCase()}`} />
                                    <Row label={t("common.rooms")} value={room.name} />
                                </div>
                                <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                                    <div className="flex justify-between"><span>{format(room.price_per_night)} × {nights} × {rooms}</span><span>{format(originalSubtotal)}</span></div>
                                    {(() => {
                                        // De-duplicate by id (combine + sale entries can repeat the same discount)
                                        // and only show discounts that affect the sale price (skip cost_only and combine entries)
                                        const seen = new Set();
                                        const visible = (discountsApplied || []).filter((d) => {
                                            if (!d || !d.amount) return false;
                                            if (d.applies_on_cost_only) return false; // cost-only: doesn't affect sale price
                                            if (d.id && seen.has(d.id)) return false;
                                            if (d.id) seen.add(d.id);
                                            return true;
                                        });
                                        const typeLabel = (type) => ({
                                            early_booking: "Erken Rezervasyon İndirimi",
                                            last_minute: "Son Dakika İndirimi",
                                            long_stay: "Uzun Konaklama İndirimi",
                                            date_range: "Sezon İndirimi",
                                            promo: "Kampanya İndirimi",
                                        })[type] || "İndirim";
                                        return visible.map((d) => (
                                            <div key={d.id} data-testid={`summary-discount-${d.id}`} className="flex justify-between text-fuchsia-600 dark:text-fuchsia-400 font-semibold">
                                                <span className="truncate max-w-[200px]">🎟️ {typeLabel(d.type)}{d.discount_type === "percentage" && d.discount_value ? ` (%${Math.round(d.discount_value)})` : ""}</span>
                                                <span>-{format(d.amount)}</span>
                                            </div>
                                        ));
                                    })()}
                                    {fakeOriginalTotal && fakeOriginalTotal > originalSubtotal && (
                                        <div data-testid="fake-discount-line" className="flex justify-between items-center text-rose-500">
                                            <span className="text-xs">Liste fiyatı</span>
                                            <span className="line-through">{format(fakeOriginalTotal)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between"><span>{t("checkout.tax_included")}</span><span>✓</span></div>
                                    {insurance && (
                                        <div data-testid="summary-insurance" className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
                                            <span>{t("checkout.insurance_title")} (1%)</span>
                                            <span>+{format(insuranceAmount)}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center">
                                    <span className="font-semibold text-slate-900 dark:text-white">{t("common.total")}</span>
                                    <span data-testid="checkout-total" className="text-2xl font-display font-bold text-sky-600">{format(total)}</span>
                                </div>
                                {selectedPlan?.cancellation && (
                                    <div className="flex items-start gap-2 text-xs text-emerald-600 dark:text-emerald-400" data-testid="checkout-cancellation">
                                        <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                        <span>{selectedPlan.cancellation}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
            <Footer />
        </div>
    );
}

function PaymentRadio({ testId, active, onClick, icon, title, desc }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`w-full flex items-start gap-3 text-left p-4 rounded-xl border-2 transition-colors ${active ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300"}`}
        >
            <span className="mt-0.5">{icon}</span>
            <span className="flex-1 min-w-0">
                <span className="block font-display font-bold text-slate-900 dark:text-white">{title}</span>
                <span className="block text-xs text-slate-500 mt-0.5">{desc}</span>
            </span>
            <span className={`mt-1 h-5 w-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${active ? "border-sky-500 bg-sky-500" : "border-slate-300 dark:border-slate-600"}`}>
                {active && <Check className="h-3 w-3 text-white" />}
            </span>
        </button>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between text-sm">
            <span className="text-slate-500">{label}</span>
            <span className="font-medium text-slate-900 dark:text-white">{value}</span>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", placeholder, required, testid }) {
    return (
        <label className="block">
            <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}{required && " *"}</span>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white"
            />
        </label>
    );
}
